import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import Exception from '@/common/models/Exception';

export default class TableColumn {
  constructor(
    key,
    type = TableColumnTypes.string,
    label, // User friendly name.
    property, // Usually the server side property name, so that it can be used for sorting.
    allowSort = true,
    orderBy = undefined,
    fragments = undefined,
    isVisible = true,
    useAsFilter = true,
    index = null, // Used to display columns in a particular order
    widthInPixels = null,
    minWidthInPixels = null,
    maxWidthInPixels = null,
    isMoney = false, // Flagged as money so that it can be blurred.
    right = false, // Align content right.
    mutation = null, // Vuex mutation used when inline editing
    editablePropertyName = null, // Class property name for inline editing.
    shouldRenderColumn = true, // Set to false if you need to use a column for filtering purposes only.
    source = null, // Data source for list filters, can be an array or a function.
    sourceFilterProperty = null, // Property being filtered on, which can be different than 'property' (e.g: advertiser_id vs advertiser.name).
    sourceLabel = 'name',
    sourceID = 'id',
    postMutationCallback = null, // Allow for custom code after a mutation was committed.
    action = null, // Vuex action to be called when inline editing changes are committed.
    getExtraValidation = null, // Function returning extra validation when inline editing.
    isEditableHandler = null, // Function returning regardless of whether a particular entity field is editable.
    editablePropertyAcceptsMultipleValues = null, // Set to ture when the property accepts multiple values from a source (e.g.: IAB categories)
    editableClassName = null, // Required when setting the mutation since we cannot find the class name at runtime.
    sourceGroupValues = null, // Used to group options in the multiselect (see venue types).
    sourceGroupLabel = null, // Used to group options in the multiselect (see venue types).
    preMutationTransform = null, // Transformation applied to the edited value before calling the mutation.
    preActionTransform = null, // Transformation applied to the edited value before calling the action.
    isDefault = false,
    sourceSupportsAutoComplete = false, // If supplying a function for the 'source' arg, this indicates that the function supports auto-completion (api call whenever search changes)
    sourceSearchTermToFiltersTransform = null, // When the source supports auto-completion this function take care of translating the search term into Filters.
    inputElementWidth = null, // Useful for setting the with of text boxes and multiselect dropdown in the bulk editing form.
    validationPropertyName = null, // In bulk editing, this is the property key used to report validation, see unit > operating hours for an example.
    canAffectAdDeliveryWhenModified = false, // Changes to this property can affect delivery.
    entityValueGetterTransform = null, // Example: transform a list of UnitLanguage objects into a list of ID so that they can be mapped to dropdown values.
    preventEditingEntityProperties = [], // For impressions goal you would specify ['budget'].
    areSourceGroupParentsSelectable = false, // In some cases like "IAB categories", the parent of a group of options can be selected.
    description = null, // will display a tooltip when hovering over a column head
    checkForEmptyness = false, // Accepts Boolean or { customComparisonValue: string[] }. Used to check if a value exists for Boolean Filter
    bulkCreateOrderIndex = 100000, // Used to sort editable field in bulk edit v4 (spreadsheets).
    isProxyForProperty = null, // { property, editablePropertyName, validationError }, see unit.external_id
    proxyPropertyMapper = null, // given a set of ids, return a collection of objects like these: [{ from, to }]
    areValuesEqual = null, // (from, to) => boolean, compare to objects and check if values of the property are equal.
    bulkInputNote = null, // Text displayed in the header of worksheet and in the reference CSV for Bulk V4.
    bulkInputExamples = null, // Examples displayed in the header of XLS worksheet and in the reference CSV for Bulk V4.
    defaultValue = null,
    mutatesProperties = null, // Whenever this property is updated ist will mutate the properties in this collection. (e.g.: budget & target impressions mutate budgetMode)
    isInlineEditingAvailable = true,
    inlineEditingOptionsSource = null, // Source to be used by TableCellEditor dropdown if the source for the filter and inline edit are differents (Venue types filter source and inline edit source have different structures and need different sources)
    isFixed = false, // Set a fixed filter that cannot be removed by clearing the filters
    sendTracking = false, // Send Google Analytics event
    numericalOperatorsToExclude = [], // Remove any of the numerical operators from the filter if the BE doesn't support the filter,
    shouldBeUppercased = true, // Column header text should be uppercased
    filterOptionsTransformation = null, // Transformation to format filter options
    customQueryParams = null, // Custom query param handler
    allowExclusions = true,
    sourceQueryParamsForReadOnly = null, // Custom query params for read-only source
    tableDownloadColumn = null, // Column to be included in the table download
    fallbackToString = null, // Fallback to string when the value is null
    toStringOverride = null, // Override the toString method
    ignoreUnmatchedValuesInSource = null, // Whether to ignore unmatched values in the source, an example is location_ids (level).
    instance = null, // Filter instance
    summary = null, // Summary of the filter as a string (ex: Status: Draft, Active)
    handleInlineEditUpdateOutsideOfTable = undefined, // Handle update outside of table (in case you don't want to go through the whole commit process of TableCellEditor while still using the inline editing)
  ) {
    this.key = key;
    this.type = type;
    this.label = label;
    this.property = property;
    this.allowSort = allowSort;
    this.orderBy = orderBy;
    this.fragments = fragments;
    this.isVisible = isVisible;
    this.useAsFilter = useAsFilter;
    this.index = index;
    this.widthInPixels = widthInPixels;
    this.minWidthInPixels = minWidthInPixels;
    this.maxWidthInPixels = maxWidthInPixels;
    this.isMoney = isMoney;
    this.right = right;
    this.mutation = mutation;
    this.editablePropertyName = editablePropertyName; // 'property' contains the server-side property name.
    this.shouldRenderColumn = shouldRenderColumn;
    this.source = source;
    this.sourceFilterProperty = sourceFilterProperty;
    this.sourceLabel = sourceLabel;
    this.sourceID = sourceID;
    this.postMutationCallback = postMutationCallback; // Used to apply other transformation on the entity after a mutation. (e.g.: update a campaign account)
    this.action = action; // The action will be inferred from the entity class but in some case it does not match. (line items)
    this.getExtraValidation = getExtraValidation;
    this.isEditableHandler = isEditableHandler;
    this.editablePropertyAcceptsMultipleValues = editablePropertyAcceptsMultipleValues;
    this.editableClassName = editableClassName;
    this.sourceGroupValues = sourceGroupValues;
    this.sourceGroupLabel = sourceGroupLabel;
    this.sourceSearchTermToFiltersTransform = sourceSearchTermToFiltersTransform;
    this.sourceSupportsAutoComplete = sourceSupportsAutoComplete;
    this.preMutationTransform = preMutationTransform;
    this.preActionTransform = preActionTransform;
    this.isDefault = isDefault;
    this.inputElementWidth = inputElementWidth;
    this.validationPropertyName = validationPropertyName;
    this.canAffectAdDeliveryWhenModified = canAffectAdDeliveryWhenModified;
    this.entityValueGetterTransform = entityValueGetterTransform;
    this.preventEditingEntityProperties = preventEditingEntityProperties;
    this.areSourceGroupParentsSelectable = areSourceGroupParentsSelectable;
    this.description = description;
    this.checkForEmptyness = checkForEmptyness;
    this.bulkCreateOrderIndex = bulkCreateOrderIndex;
    this.isProxyForProperty = isProxyForProperty;
    this.proxyPropertyMapper = proxyPropertyMapper;
    this.areValuesEqual = areValuesEqual;
    this.bulkInputNote = bulkInputNote;
    this.bulkInputExamples = bulkInputExamples;
    this.defaultValue = defaultValue;
    this.mutatesProperties = mutatesProperties;
    this.isInlineEditingAvailable = isInlineEditingAvailable;
    this.inlineEditingOptionsSource = inlineEditingOptionsSource;
    this.isFixed = isFixed;
    this.sendTracking = sendTracking;
    this.numericalOperatorsToExclude = numericalOperatorsToExclude;
    this.shouldBeUppercased = shouldBeUppercased;
    this.filterOptionsTransformation = filterOptionsTransformation;
    this.customQueryParams = customQueryParams;
    this.allowExclusions = allowExclusions;
    this.sourceQueryParamsForReadOnly = sourceQueryParamsForReadOnly;
    this.tableDownloadColumn = tableDownloadColumn;
    this.fallbackToString = fallbackToString;
    this.toStringOverride = toStringOverride;
    this.ignoreUnmatchedValuesInSource = ignoreUnmatchedValuesInSource;
    this.instance = instance;
    this.summary = summary;
    this.handleInlineEditUpdateOutsideOfTable = handleInlineEditUpdateOutsideOfTable;
    //
    // Remember to modify the clone and fromObject methods if you
    // add properties here.
    //
  }

  get style() {
    let style = '';
    if (this.widthInPixels !== null) {
      style = `${style}width: ${this.widthInPixels}px;`;
    }
    if (this.maxWidthInPixels !== null) {
      style = `${style};max-width: ${this.maxWidthInPixels}px;`;
    }
    if (this.minWidthInPixels !== null) {
      style = `${style};min-width: ${this.minWidthInPixels}px;`;
    }
    return style;
  }

  get apiModelProperty() {
    return this.isProxyForProperty || this.property;
  }

  get modelProperty() {
    if (this.isProxyForProperty != null) {
      return this.isProxyForProperty.editablePropertyName || this.isProxyForProperty.property;
    }
    return this.validationPropertyName || this.editablePropertyName || this.property;
  }

  clone() {
    return new TableColumn(
      this.key,
      this.type,
      this.label,
      this.property,
      this.allowSort,
      this.orderBy,
      this.fragments,
      this.isVisible,
      this.useAsFilter,
      this.index,
      this.widthInPixels,
      this.minWidthInPixels,
      this.maxWidthInPixels,
      this.isMoney,
      this.right,
      this.mutation,
      this.editablePropertyName,
      this.shouldRenderColumn,
      this.source,
      this.sourceFilterProperty,
      this.sourceLabel,
      this.sourceID,
      this.postMutationCallback,
      this.action,
      this.getExtraValidation,
      this.isEditableHandler,
      this.editablePropertyAcceptsMultipleValues,
      this.editableClassName,
      this.sourceGroupValues,
      this.sourceGroupLabel,
      this.preMutationTransform,
      this.preActionTransform,
      this.isDefault,
      this.sourceSupportsAutoComplete,
      this.sourceSearchTermToFiltersTransform,
      this.inputElementWidth,
      this.validationPropertyName,
      this.canAffectAdDeliveryWhenModified,
      this.entityValueGetterTransform,
      this.preventEditingEntityProperties,
      this.areSourceGroupParentsSelectable,
      this.description,
      this.checkForEmptyness,
      this.bulkCreateOrderIndex,
      this.isProxyForProperty,
      this.proxyPropertyMapper,
      this.areValuesEqual,
      this.bulkInputNote,
      this.bulkInputExamples,
      this.defaultValue,
      this.mutatesProperties,
      this.isInlineEditingAvailable,
      this.inlineEditingOptionsSource,
      this.isFixed,
      this.sendTracking,
      this.numericalOperatorsToExclude,
      this.shouldBeUppercased,
      this.filterOptionsTransformation,
      this.customQueryParams,
      this.allowExclusions,
      this.sourceQueryParamsForReadOnly,
      this.tableDownloadColumn,
      this.fallbackToString,
      this.toStringOverride,
      this.ignoreUnmatchedValuesInSource,
      this.instance,
      this.summary,
      this.handleInlineEditUpdateOutsideOfTable,
    );
  }

  static fromObject(col) {
    const validProps = [
      'key',
      'type',
      'label',
      'property',
      'allowSort',
      'orderBy',
      'fragments',
      'isVisible',
      'useAsFilter',
      'index',
      'widthInPixels',
      'minWidthInPixels',
      'maxWidthInPixels',
      'isMoney',
      'right',
      'mutation',
      'editablePropertyName',
      'shouldRenderColumn',
      'source',
      'sourceFilterProperty',
      'sourceLabel',
      'sourceID',
      'sourceSearchTermToFiltersTransform',
      'sourceSupportsAutoComplete',
      'postMutationCallback',
      'action',
      'getExtraValidation',
      'isEditableHandler',
      'editablePropertyAcceptsMultipleValues',
      'editableClassName',
      'sourceGroupValues',
      'sourceGroupLabel',
      'preMutationTransform',
      'preActionTransform',
      'isDefault',
      'inputElementWidth',
      'validationPropertyName',
      'canAffectAdDeliveryWhenModified',
      'entityValueGetterTransform',
      'preventEditingEntityProperties',
      'areSourceGroupParentsSelectable',
      'description',
      'checkForEmptyness',
      'bulkCreateOrderIndex',
      'isProxyForProperty',
      'proxyPropertyMapper',
      'areValuesEqual',
      'bulkInputNote',
      'bulkInputExamples',
      'defaultValue',
      'mutatesProperties',
      'isInlineEditingAvailable',
      'inlineEditingOptionsSource',
      'isFixed',
      'sendTracking',
      'numericalOperatorsToExclude',
      'shouldBeUppercased',
      'filterOptionsTransformation',
      'customQueryParams',
      'allowExclusions',
      'sourceQueryParamsForReadOnly',
      'tableDownloadColumn',
      'fallbackToString',
      'toStringOverride',
      'ignoreUnmatchedValuesInSource',
      'instance',
      'summary',
      'handleInlineEditUpdateOutsideOfTable',
    ];

    const invalidProperties = Object.keys(col).filter((x) => !validProps.includes(x));

    if (invalidProperties.length > 0) {
      throw new Exception(`Invalid properties: ${invalidProperties.join(', ')}`);
    }

    if (col.mutation != null && col.editableClassName == null) {
      throw new Exception('editableClassName is required when supplying a mutation.');
    }

    return new TableColumn(
      col.key,
      col.type,
      col.label,
      col.property,
      col.allowSort,
      col.orderBy,
      col.fragments,
      col.isVisible,
      col.useAsFilter,
      col.index,
      col.widthInPixels,
      col.minWidthInPixels,
      col.maxWidthInPixels,
      col.isMoney,
      col.right,
      col.mutation,
      col.editablePropertyName,
      col.shouldRenderColumn,
      col.source,
      col.sourceFilterProperty,
      col.sourceLabel,
      col.sourceID,
      col.postMutationCallback,
      col.action,
      col.getExtraValidation,
      col.isEditableHandler,
      col.editablePropertyAcceptsMultipleValues,
      col.editableClassName,
      col.sourceGroupValues,
      col.sourceGroupLabel,
      col.preMutationTransform,
      col.preActionTransform,
      col.isDefault,
      col.sourceSupportsAutoComplete,
      col.sourceSearchTermToFiltersTransform,
      col.inputElementWidth,
      col.validationPropertyName,
      col.canAffectAdDeliveryWhenModified,
      col.entityValueGetterTransform,
      col.preventEditingEntityProperties,
      col.areSourceGroupParentsSelectable,
      col.description,
      col.checkForEmptyness,
      col.bulkCreateOrderIndex,
      col.isProxyForProperty,
      col.proxyPropertyMapper,
      col.areValuesEqual,
      col.bulkInputNote,
      col.bulkInputExamples,
      col.defaultValue,
      col.mutatesProperties,
      col.isInlineEditingAvailable,
      col.inlineEditingOptionsSource,
      col.isFixed,
      col.sendTracking,
      col.numericalOperatorsToExclude,
      col.shouldBeUppercased,
      col.filterOptionsTransformation,
      col.customQueryParams,
      col.allowExclusions,
      col.sourceQueryParamsForReadOnly,
      col.tableDownloadColumn,
      col.fallbackToString,
      col.toStringOverride,
      col.ignoreUnmatchedValuesInSource,
      col.instance,
      col.summary,
      col.handleInlineEditUpdateOutsideOfTable,
    );
  }
}
